import React, { memo, useRef } from 'react';
import { Collapse, Fade, LinearProgress, Theme, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Stepper from 'components/common/Stepper';

interface IProps {
  activeStep?: number;
  className?: string;
  children: React.ReactNode;
  connector?: React.ReactElement;
  collapsed?: boolean;
  background?: 'default' | 'transparent';
}

interface IPropsStyles {
  collapsed?: boolean;
}

const useStyles = makeStyles<IPropsStyles>()((theme, params) => ({
  container: {
    position: 'relative',
    padding: theme.spacing(3, 4),
    background: theme.palette.background.default,
  },
  progressBarContainer: {
    position: 'absolute',
    width: '100%',
    top: 0,
    zIndex: 2,
    padding: theme.spacing(3, 4),
    background: params?.collapsed ? '#fff' : theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1, 2, 1),
    },
  },
  stepper: {
    [theme.breakpoints.down('md')]: {
      paddingTop: 0,
    },
  },
}));

const CollapsibleStepper: React.FC<React.PropsWithChildren<IProps>> = ({
  activeStep,
  connector,
  background,
  collapsed,
  children,
  className,
}) => {
  const { classes, cx } = useStyles({ collapsed });
  const progressBarRef = useRef(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const collapsedHeight = progressBarRef.current?.offsetHeight || 0;
  const totalSteps = React.Children.toArray(children).length;
  const progress = (activeStep / (totalSteps - 1)) * 100;

  return (
    <div className={cx(classes.container, className)}>
      <Fade in={collapsed}>
        <div className={classes.progressBarContainer}>
          <LinearProgress ref={progressBarRef} value={progress} variant="determinate" />
        </div>
      </Fade>
      <Collapse in={!collapsed} collapsedSize={collapsedHeight}>
        <Stepper
          className={classes.stepper}
          activeStep={activeStep}
          alternativeLabel={isMobile}
          connector={connector}
          background={background}
        >
          {children}
        </Stepper>
      </Collapse>
    </div>
  );
};

export default memo(CollapsibleStepper);
